import { DepartmentLabel, useDepartmentText } from 'model/DepartmentTerminology';
import useDepartmentUiMode from 'model/useDepartmentUiMode';
import { useForm } from '@mantine/form';
import { noop } from 'lodash';
import { FormProps } from 'components/forms/FormProps';
import createValidator from 'components/forms/validators/create-validator';
import required from 'components/forms/validators/rules/rule-required';
import useLoadingAction from 'hooks/use-loading-action';
import { Anchor, Box, Checkbox, Group, Select, SimpleGrid, Stack, Switch, TextInput } from '@mantine/core';
import FormInputGroup from 'components/forms/FormInputGroup';
import { OrganizationSelect } from 'components/selects/OrganizationSelect';
import FormWrapper from 'components/forms/FormWrapper';
import OverlayForm from 'components/modals/OverlayForm';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import P1Regular from 'components/typography/P1Regular';
import P1RegularLink from 'components/typography/P1RegularLink';
import PersonWithPermissions from 'components/PersonWithPermissions';
import { usePickUsers } from 'components/modals/pick-users-modal/PickUsersProvider';
import { IconCheck, IconPlus } from '@tabler/icons-react';
import Toast from 'components/Toast';
import { DepartmentContactPersonSelect } from 'components/selects/DepartmentContactPersonSelect';
import P2Regular from 'components/typography/P2Regular';
import { useDirtyFormHandler } from 'hooks/use-dirty-form-handler';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import Button from 'components/Button';
import { useApi } from 'api/api-context';
import panic from 'errors/panic';
import { DepartmentListResponse } from 'api/actions/department-list/department-list-response';
import { HierarchyAvailableLevelsResponse } from 'api/actions/hierarchy-available-levels/hierarchy-available-levels-response';
import { DepartmentSelect } from 'components/selects/DepartmentSelect';
import {
  ALL_ORGANIZATION_MODULES_DISABLED,
  OrganizationModuleFlags,
} from 'features/organization-modules/use-organization-modules';
import useLocalStorage from 'hooks/use-local-storage';
import P1Medium from 'components/typography/P1Medium';
import RepeatCreationLabel from 'components/forms/RepeatCreationLabel';

/**
 * The data collected from the form.
 */
export interface DepartmentFormData {
  departmentName: string;
  departmentNumber?: string;
  streetAddress: string;
  city: string;
  zip: string;
  country: string;
  contactPersonId?: string;
  organizationId: string;
  hierarchyLevelSlug: string;
  parentDepartmentId: string;
  supportsKep?: boolean;
  organizationPeople?: {
    [userId: string]: {
      checked: boolean;
      permissions: number[];
      templateName: string;
    };
  };
  peopleInAncestors: {
    userId: number;
    permissions: number[];
    templateName: string;
    sourceDepartmentId: number;
    sourceDepartmentName: string;
    sourceDepartmentPath: string;
  }[];
  people: {
    [userId: string]: {
      checked: boolean;
      deleted: boolean;
      permissions: number[];
      templateName: string;
      isNew: boolean;
    };
  };
}

interface DisplayedDepartmentPerson {
  userId: number;
  direct?: DepartmentFormData['people'][string];
  inheritedSources: {
    key: string;
    label: string;
    permissions: number[];
    templateSlug: string;
  }[];
}

/**
 * The default initial values.
 */
export const departmentFormInitialValues: DepartmentFormData = {
  departmentName: '',
  departmentNumber: '',
  streetAddress: '',
  city: '',
  zip: '',
  country: '',
  contactPersonId: '',
  organizationId: '',
  hierarchyLevelSlug: '',
  parentDepartmentId: '',
  supportsKep: true,
  organizationPeople: {},
  peopleInAncestors: [],
  people: {},
};

/**
 * Parameters of the DepartmentForm.
 */
export interface DepartmentFormProps extends FormProps<DepartmentFormData> {
  readOnly?: Partial<Record<keyof DepartmentFormData, boolean>>;
  notAllowed?: Partial<Record<keyof DepartmentFormData, boolean>>;
  primaryButtonText?: string;
  primaryIcon?: React.ReactNode;
  moduleFlags?: OrganizationModuleFlags;
  currentDepartmentId?: number;
  footerRightSide?: React.ReactNode;
  onSecondaryClick?: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  onBusyChange?: (busy: boolean) => void;
  selectWithinPortal?: boolean;
  embedded?: boolean;
  inferHierarchyLevelFromParent?: boolean;
  repeatCreationLocks?: {
    lockedFields: DepartmentRepeatFieldLocks;
    onToggle: (field: DepartmentRepeatField) => void;
  };
}

export interface DepartmentFormHandle {
  markClean: () => void;
}

export type DepartmentRepeatField = Exclude<
  keyof DepartmentFormData,
  'organizationPeople' | 'peopleInAncestors' | 'people'
>;

export type DepartmentRepeatFieldLocks = Record<DepartmentRepeatField, boolean>;

/**
 * Form for creating and editing department.
 */
const DepartmentForm = forwardRef<DepartmentFormHandle, DepartmentFormProps>(function DepartmentForm(
  {
    initialValues = departmentFormInitialValues,
    onSubmit = noop,
    readOnly = {},
    notAllowed = {},
    primaryButtonText = 'Uložiť',
    primaryIcon = <IconCheck stroke="1.5" height={24} width={24} />,
    moduleFlags = ALL_ORGANIZATION_MODULES_DISABLED,
    currentDepartmentId,
    footerRightSide,
    onSecondaryClick,
    onDirtyChange,
    onBusyChange,
    selectWithinPortal = true,
    embedded = false,
    inferHierarchyLevelFromParent = true,
    repeatCreationLocks,
  }: DepartmentFormProps,
  ref
) {
  const departmentText = useDepartmentText();
  const { mode } = useDepartmentUiMode();
  const { getAction } = useApi();
  const [{ loading }, submit] = useLoadingAction(onSubmit);
  const { pickUsers } = usePickUsers();
  const { isTablet } = useResponsiveDesign();
  const [organizationDepartments, setOrganizationDepartments] = useState<DepartmentListResponse>([]);
  const [organizationLevels, setOrganizationLevels] = useState<HierarchyAvailableLevelsResponse[number]['levels']>([]);
  const [permissionVisibility, , updatePermissionVisibility] = useLocalStorage(
    'objekto.permissions.hide-inherited-only',
    { hideInheritedOnly: false }
  );

  const form = useForm<DepartmentFormData>({
    initialValues,
    validate: {
      departmentName: createValidator([required]),
      organizationId: createValidator([required]),
      hierarchyLevelSlug: mode === 'legacy' ? () => null : createValidator([required]),
      parentDepartmentId: (value, values) => {
        if (mode === 'legacy') return null;
        const level = organizationLevels.find(({ slug }) => slug === values.hierarchyLevelSlug);
        return level && level.levelOrder > 0 && !value ? required(value)?.message ?? null : null;
      },
    },
  });
  const liveForm = useRef(form);
  liveForm.current = form;
  const previousOrganizationId = useRef(
    repeatCreationLocks?.lockedFields.contactPersonId ? initialValues.organizationId : undefined
  );

  useImperativeHandle(ref, () => ({ markClean: () => form.resetDirty() }));

  useDirtyFormHandler(form);

  useEffect(() => {
    onDirtyChange?.(form.isDirty());
  }, [form.values, onDirtyChange]);

  useEffect(() => {
    onBusyChange?.(loading);
  }, [loading, onBusyChange]);

  const displayedPeople = useMemo(() => {
    const people = new Map<number, DisplayedDepartmentPerson>();

    /** Returns the single display model for a user, creating it when needed. */
    const getPerson = (userId: number) => {
      const person = people.get(userId) ?? { userId, inheritedSources: [] };
      people.set(userId, person);
      return person;
    };

    Object.entries(form.values.organizationPeople ?? {}).forEach(([userId, person]) => {
      if (!person.checked) return;
      getPerson(Number(userId)).inheritedSources.push({
        key: 'organization',
        label: 'Oprávnenia z organizácie',
        permissions: person.permissions,
        templateSlug: person.templateName,
      });
    });

    [...form.values.peopleInAncestors]
      .sort((left, right) => {
        const leftDepth = left.sourceDepartmentPath.split(' / ').length;
        const rightDepth = right.sourceDepartmentPath.split(' / ').length;

        return leftDepth - rightDepth || left.sourceDepartmentPath.localeCompare(right.sourceDepartmentPath, 'sk');
      })
      .forEach((person) => {
        getPerson(person.userId).inheritedSources.push({
          key: `department-${person.sourceDepartmentId}`,
          label: departmentText(
            `Oprávnenia z položky úrovne ${person.sourceDepartmentPath}`,
            `Oprávnenia zo strediska ${person.sourceDepartmentPath}`
          ),
          permissions: person.permissions,
          templateSlug: person.templateName,
        });
      });

    Object.entries(form.values.people).forEach(([userId, person]) => {
      if (person.checked) getPerson(Number(userId)).direct = person;
    });

    return Array.from(people.values());
  }, [departmentText, form.values.organizationPeople, form.values.peopleInAncestors, form.values.people]);

  const displayMainAddButton = displayedPeople.length === 0;
  const editableDisplayedPeople = permissionVisibility.hideInheritedOnly
    ? displayedPeople.filter(({ direct }) => Boolean(direct?.permissions.length))
    : displayedPeople;
  const inheritedOnlyPeople = permissionVisibility.hideInheritedOnly
    ? displayedPeople.filter(
        ({ direct, inheritedSources }) =>
          !direct?.permissions.length && inheritedSources.some(({ permissions }) => permissions.length > 0)
      )
    : [];

  /**
   * Callback for when user clicks on the "Pridať ľudí" button.
   */
  const addPeopleToDepartment = () => {
    pickUsers({
      title: <DepartmentLabel objectLabel="Pridať ľudí do položky úrovne" departmentLabel="Pridať ľudí do strediska" />,
      hiddenUsers: Object.keys(form.values.people)
        .filter((userId) => form.values.people[Number(userId)].checked)
        .map(Number),
      onPick: ({ pickedUsers, permissions, templateName }) => {
        pickedUsers.forEach((userId: number) => {
          const oldChecked = form.values.people[userId]?.checked ?? false;
          const newChecked = true;

          form.setFieldValue(`people.${userId}`, {
            checked: newChecked,
            deleted: false,
            permissions,
            templateName,
            isNew: !oldChecked && newChecked,
          });
        });
      },
      inForm: 'department',
      initialTemplateName: 'no-template',
      initialPermissions: [],
      moduleFlags,
      pickUsers: true,
      pickPermissions: true,
    });
  };

  useEffect(() => {
    if (!readOnly.organizationId && previousOrganizationId.current !== form.values.organizationId) {
      form.setFieldValue('contactPersonId', '');
    }
    previousOrganizationId.current = form.values.organizationId;
  }, [form.values.organizationId]);

  useEffect(() => {
    if (!form.values.organizationId) {
      setOrganizationDepartments([]);
      setOrganizationLevels([]);
      return;
    }

    let active = true;

    Promise.all([
      getAction('DepartmentList')({
        query: {
          filters: {
            'organizationId.eq': Number(form.values.organizationId),
            'showDiscarded.eq': 0,
            'permissionSlug.eq': 'manage-departments',
          },
        },
      }),
      getAction('OrganizationList')({
        query: { filters: { 'showDiscarded.eq': 0, 'permissionSlug.eq': 'manage-departments' } },
      }),
      getAction('HierarchyAvailableLevels')({
        query: { filters: { 'permissionSlug.eq': 'manage-departments' } },
      }),
    ])
      .then(([departments, organizations, hierarchies]) => {
        if (!active) return;

        const currentForm = liveForm.current;
        const currentValues = currentForm.values;

        const filteredDepartments = departments.filter(
          ({ breadcrumbs }) =>
            !currentDepartmentId || !breadcrumbs.some(({ departmentId }) => departmentId === currentDepartmentId)
        );
        const organization = organizations.find(
          ({ organizationId }) => String(organizationId) === currentValues.organizationId
        );
        const levels =
          hierarchies
            .find(({ hierarchyId }) => hierarchyId === organization?.hierarchyAssignment.hierarchyId)
            ?.levels.slice()
            .sort((left, right) => left.levelOrder - right.levelOrder) ?? [];

        setOrganizationDepartments(filteredDepartments);
        setOrganizationLevels(levels);

        if (!levels.some(({ slug }) => slug === currentValues.hierarchyLevelSlug)) {
          const selectedParent = filteredDepartments.find(
            ({ departmentId }) => String(departmentId) === currentValues.parentDepartmentId
          );
          const inferredLevel =
            selectedParent && inferHierarchyLevelFromParent
              ? levels.find(({ levelOrder }) => levelOrder === selectedParent.levelOrder + 1)
              : undefined;
          const inferredLevelSlug = inferredLevel?.slug ?? '';
          if (!currentDepartmentId && selectedParent && inferredLevel && !currentForm.isDirty()) {
            currentForm.resetDirty({ ...currentValues, hierarchyLevelSlug: inferredLevelSlug });
          }
          currentForm.setFieldValue('hierarchyLevelSlug', inferredLevelSlug);
        }
      })
      .catch((error) => {
        if (active) panic(error);
      });

    return () => {
      active = false;
    };
  }, [form.values.organizationId, getAction, currentDepartmentId]);

  const selectedLevel = organizationLevels.find(({ slug }) => slug === form.values.hierarchyLevelSlug);
  const parentLevelOrder = selectedLevel ? selectedLevel.levelOrder - 1 : -1;

  const FormShell = embedded ? OverlayForm : FormWrapper;

  /** Adds retention controls only while repeat creation is enabled. */
  function fieldLabel(field: DepartmentRepeatField, label: string) {
    return {
      'aria-label': label,
      label: repeatCreationLocks ? (
        <RepeatCreationLabel
          label={label}
          locked={repeatCreationLocks.lockedFields[field]}
          onToggle={() => repeatCreationLocks.onToggle(field)}
          withinPortal={selectWithinPortal}
        />
      ) : (
        label
      ),
    };
  }

  return (
    <FormShell
      onSubmit={form.onSubmit(mode === 'legacy' ? (values) => submit({ ...values, parentDepartmentId: '' }) : submit)}
      loading={loading}
      primaryButtonText={primaryButtonText}
      primaryIcon={primaryIcon}
      skipSecondaryButtonConfirm={Boolean(onSecondaryClick) || !form.isDirty()}
      footerRightSide={footerRightSide}
      onSecondaryClick={onSecondaryClick}
    >
      <FormInputGroup groupTitle="Organizácia">
        <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
          <OrganizationSelect
            withinPortal={selectWithinPortal}
            {...fieldLabel('organizationId', 'Organizácia')}
            size="lg"
            placeholder="Vyberte organizáciu"
            readOnly={readOnly.organizationId}
            showDiscarded={false}
            autoSelectSingleResult
            permissionSlug="manage-departments"
            name="objekto-organizationId"
            {...form.getInputProps('organizationId')}
            onChange={(value) => {
              form.setFieldValue('organizationId', value ? String(value) : '');
              form.setFieldValue('hierarchyLevelSlug', '');
              form.setFieldValue('parentDepartmentId', '');
            }}
          />
        </SimpleGrid>
      </FormInputGroup>

      {mode === 'tree' && (
        <FormInputGroup groupTitle="Zaradenie v hierarchii">
          <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
            <Select
              withinPortal={selectWithinPortal}
              size="lg"
              {...fieldLabel('hierarchyLevelSlug', 'Úroveň')}
              placeholder="Vyberte úroveň"
              data={organizationLevels.map(({ label, slug }) => ({ label, value: slug }))}
              disabled={!form.values.organizationId}
              readOnly={readOnly.hierarchyLevelSlug}
              {...form.getInputProps('hierarchyLevelSlug')}
              onChange={(value) => {
                form.setFieldValue('hierarchyLevelSlug', value ?? '');
                form.setFieldValue('parentDepartmentId', '');
              }}
            />
            {selectedLevel && selectedLevel.levelOrder > 0 && (
              <DepartmentSelect
                size="lg"
                {...fieldLabel('parentDepartmentId', departmentText('Nadradená položka úrovne', 'Nadradené stredisko'))}
                placeholder={departmentText('Vyberte nadradenú položku úrovne', 'Vyberte nadradené stredisko')}
                allowLegacy={false}
                showDiscarded={false}
                organizationId={Number(form.values.organizationId)}
                preloadedDepartments={organizationDepartments}
                filterData={(departments) =>
                  departments.filter((department) => department.breadcrumbs.at(-1)?.levelOrder === parentLevelOrder)
                }
                readOnly={readOnly.parentDepartmentId}
                {...form.getInputProps('parentDepartmentId')}
                onChange={(value) => form.setFieldValue('parentDepartmentId', value ?? '')}
              />
            )}
          </SimpleGrid>
        </FormInputGroup>
      )}

      <FormInputGroup groupTitle="Základné info">
        <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
          <TextInput
            {...fieldLabel('departmentName', 'Názov')}
            placeholder={departmentText('Názov položky úrovne', 'Názov strediska')}
            size="lg"
            readOnly={readOnly.departmentName}
            name="objekto-departmentName"
            {...form.getInputProps('departmentName')}
          />
          <TextInput
            {...fieldLabel('departmentNumber', departmentText('Číslo položky úrovne', 'Číslo strediska'))}
            placeholder={departmentText('Číslo položky úrovne', 'Číslo strediska')}
            size="lg"
            readOnly={readOnly.departmentNumber}
            name="objekto-departmentNumber"
            {...form.getInputProps('departmentNumber')}
          />
        </SimpleGrid>
        <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
          <TextInput
            {...fieldLabel('streetAddress', 'Adresa')}
            placeholder="Ulica a číslo"
            size="lg"
            readOnly={readOnly.streetAddress}
            name="051f13c0-6aec-4932-9fb4-ed51732b024f"
            {...form.getInputProps('streetAddress')}
          />
          <TextInput
            {...fieldLabel('city', 'Mesto')}
            placeholder="Mesto"
            size="lg"
            readOnly={readOnly.city}
            name="f56e4b81-dc59-499b-abbb-6df3e78ee0c3"
            {...form.getInputProps('city')}
          />
        </SimpleGrid>
        <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
          <TextInput
            {...fieldLabel('zip', 'PSČ')}
            placeholder="PSČ"
            size="lg"
            readOnly={readOnly.zip}
            name="1e43089c-650c-4596-af04-5c5d85ccf2be"
            {...form.getInputProps('zip')}
          />
          <TextInput
            {...fieldLabel('country', 'Štát')}
            placeholder="Štát"
            size="lg"
            readOnly={readOnly.country}
            name="630f9799-cecd-40c5-b690-27589690f025"
            {...form.getInputProps('country')}
          />
        </SimpleGrid>
      </FormInputGroup>

      <FormInputGroup groupTitle="Kontaktná osoba">
        <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
          {form.values.organizationId ? (
            <DepartmentContactPersonSelect
              withinPortal={selectWithinPortal}
              {...fieldLabel('contactPersonId', 'Kontaktná osoba')}
              size="lg"
              placeholder="Vyberte kontaktnú osobu"
              organizationId={Number(form.values.organizationId)}
              name="objekto-contactPersonId"
              clearable
              {...form.getInputProps('contactPersonId')}
            />
          ) : (
            <Stack py={16}>
              <P2Regular color="gray.8">Najskôr vyberte organizáciu</P2Regular>
            </Stack>
          )}
        </SimpleGrid>
      </FormInputGroup>

      <FormInputGroup groupTitle="KEP podpora">
        <Checkbox
          {...fieldLabel('supportsKep', departmentText('Položka úrovne podporuje KEP', 'Stredisko podporuje KEP'))}
          size="md"
          {...form.getInputProps('supportsKep', { type: 'checkbox' })}
        />
      </FormInputGroup>

      {readOnly.people ? (
        <FormInputGroup groupTitle={departmentText('Ľudia v položke úrovne', 'Ľudia v stredisku')}>
          <Toast
            type="info"
            message={departmentText(
              'Ľudí v položke úrovne budete môcť nastaviť až po vytvorení položky úrovne',
              'Ľudí v stredisku budete môcť nastaviť až po vytvorení strediska'
            )}
            withCloseButton={false}
          />
        </FormInputGroup>
      ) : notAllowed.people ? (
        <FormInputGroup groupTitle={departmentText('Ľudia v položke úrovne', 'Ľudia v stredisku')}>
          <Toast
            type="fail"
            message={departmentText(
              'Nemáte oprávnenie spravovať ľudí v položke úrovne',
              'Nemáte oprávnenie spravovať ľudí v stredisku'
            )}
            withCloseButton={false}
          />
        </FormInputGroup>
      ) : (
        <>
          <FormInputGroup
            groupTitle={departmentText('Ľudia v položke úrovne', 'Ľudia v stredisku')}
            infoBox={
              <Box ml="auto">
                <Switch
                  checked={permissionVisibility.hideInheritedOnly}
                  label="Skryť položky iba so zdedenými oprávneniami"
                  onChange={(event) => updatePermissionVisibility({ hideInheritedOnly: event.currentTarget.checked })}
                />
              </Box>
            }
          >
            {displayMainAddButton && (
              <Group pt={26} spacing={8}>
                <P1Regular color="gray.6">
                  {departmentText(
                    'Položka úrovne zatiaľ nemá priradených žiadnych ľudí.',
                    'Stredisko zatiaľ nemá priradených žiadnych ľudí.'
                  )}
                </P1Regular>
                <Anchor onClick={addPeopleToDepartment}>
                  <P1RegularLink span color="red.8">
                    Pridať ľudí
                  </P1RegularLink>
                </Anchor>
              </Group>
            )}

            {editableDisplayedPeople.length > 0 && (
              <SimpleGrid cols={2} spacing={isTablet ? 20 : 40} verticalSpacing={24} pt={26}>
                {editableDisplayedPeople.map(({ userId, direct, inheritedSources }) => (
                  <PersonWithPermissions
                    key={userId}
                    userId={userId}
                    permissions={direct?.permissions ?? []}
                    templateSlug={direct?.templateName ?? 'no-template'}
                    value={form.values.people}
                    inForm="department"
                    moduleFlags={moduleFlags}
                    updatePermissionsForPerson={({ permissions, templateName }) => {
                      form.setFieldValue(`people.${userId}`, {
                        checked: true,
                        deleted: false,
                        permissions,
                        templateName,
                        isNew: direct?.checked ? direct.isNew : true,
                      });
                    }}
                    onUserDelete={(deletedUserId) => {
                      form.setFieldValue(`people.${deletedUserId}`, {
                        checked: false,
                        permissions: [],
                        deleted: true,
                        isNew: false,
                      });
                    }}
                    hideDelete={!direct || inheritedSources.some(({ permissions }) => permissions.length > 0)}
                    summarizeInheritedPermissions={
                      permissionVisibility.hideInheritedOnly &&
                      inheritedSources.some(({ permissions }) => permissions.length > 0)
                    }
                    permissionSources={
                      inheritedSources.length > 0
                        ? [
                            ...inheritedSources,
                            ...(direct
                              ? [
                                  {
                                    key: 'direct',
                                    label: 'Priame oprávnenia',
                                    permissions: direct.permissions,
                                    templateSlug: direct.templateName,
                                  },
                                ]
                              : []),
                          ]
                        : undefined
                    }
                  />
                ))}
              </SimpleGrid>
            )}

            {inheritedOnlyPeople.length > 0 && (
              <Stack mt={24} spacing={8}>
                <P1Medium color="gray.8">Iba so zdedenými oprávneniami</P1Medium>
                <Group spacing={8}>
                  {inheritedOnlyPeople.map(({ userId, inheritedSources }) => (
                    <PersonWithPermissions
                      key={userId}
                      compact
                      userId={userId}
                      permissions={[]}
                      templateSlug="no-template"
                      value={form.values.people}
                      inForm="department"
                      moduleFlags={moduleFlags}
                      permissionSources={inheritedSources}
                      updatePermissionsForPerson={noop}
                      onUserDelete={noop}
                    />
                  ))}
                </Group>
              </Stack>
            )}
          </FormInputGroup>

          {!displayMainAddButton && (
            <Box pl={isTablet ? 20 : 40} pb={24}>
              <Button size="md" variant="subtle" leftIcon={<IconPlus stroke="1.5" />} onClick={addPeopleToDepartment}>
                Pridať ľudí
              </Button>
            </Box>
          )}
        </>
      )}
    </FormShell>
  );
});

export default DepartmentForm;
